// Экран карточки: прочитать запись по адресу и отдать её конструктору.
//
// ЗАЧЕМ ОТДЕЛЬНО ОТ Card.tsx. `Card` умеет показать УЖЕ прочитанную запись — на этом стоят
// проверки: подсунул значения, посмотрел разметку, никакой сети. `CardScreen` отвечает за
// то, что происходит ДО записи: её может не быть, источник может не ответить, а может
// оказаться, что карточку заводят заново. Это три разных ответа, и подменять их друг
// другом нельзя — «записи нет» и «источник не ответил» ведут человека в разные стороны.
//
// ЧТО ПОДКЛЮЧИТСЯ ПОЗЖЕ. Пока источник поддельный (`fake.ts`): своя схема пишется
// параллельно. Когда она появится, меняется ОДИН проп — `источник`. Ни описания сущностей,
// ни конструктор, ни этот экран о смене транспорта не узнают.

import { Card } from './Card'
import { поддельныйИсточник } from './fake'
import { описание as описаниеПоКоду, ОПИСАНИЯ } from './specs'
import type { ИсточникКарточек } from './source'
import { пустыеЗначения } from './useCardForm'
import type { ЗаписьКарточки, Сущность } from './types'
import { useИсточник } from './useSourceData'

interface Props {
  сущность: Сущность
  /** id записи. `new` — карточка заводится с нуля. */
  id: string
  источник?: ИсточникКарточек
  безПравки?: boolean
}

export function CardScreen({ сущность, id, источник = поддельныйИсточник, безПравки }: Props) {
  const оп = ОПИСАНИЯ[сущность] ? описаниеПоКоду(сущность) : undefined
  const новая = id === 'new'

  const { данные, отказ, ждём } = useИсточник<ЗаписьКарточки | null>(
    () => (новая || !оп
      ? Promise.resolve(null)
      : источник.запись(сущность, id)),
    [источник, сущность, id, новая, оп],
  )

  if (!оп) {
    return (
      <div className="card">
        <div className="error">Такой сущности в реестре карточек нет: {сущность}</div>
        <div className="hint">
          Карточка описывается данными: чтобы она появилась, нужен файл описания
          в src/cards/specs и строка в реестре. Пустой экран здесь означал бы «нет записей».
        </div>
      </div>
    )
  }

  if (новая) {
    const пустая: ЗаписьКарточки = {
      id: 'new',
      ревизия: 0,
      состояние: 'draft',
      значения: пустыеЗначения(оп),
    }
    return <Карточка описание={оп} запись={пустая} источник={источник} безПравки={безПравки} />
  }

  if (отказ) {
    return (
      <div className="card">
        <div className="error">Карточка не прочиталась: {отказ}</div>
        <div className="hint">
          Это отказ источника, а не отсутствие записи. Пустая карточка здесь означала бы,
          что запись есть и она пустая, — а мы этого не знаем.
        </div>
      </div>
    )
  }

  if (ждём) {
    return (
      <div className="card">
        {[70, 90, 55, 80].map((w) => (
          <div key={w} className="skeleton" style={{ width: `${w}%`, marginBottom: 10 }} />
        ))}
      </div>
    )
  }

  if (!данные) {
    return (
      <div className="empty">
        <div className="empty-title">Записи нет</div>
        <div className="empty-hint">
          {оп.один} с таким номером в базе не заведена или её удалили. Это ответ «не найдено»,
          а не «источник молчит».
        </div>
      </div>
    )
  }

  return <Карточка описание={оп} запись={данные} источник={источник} безПравки={безПравки} />
}

/** Обёртка ровно ради одного: реестр описаний нужен ссылкам на связанные карточки. */
function Карточка({
  описание, запись, источник, безПравки,
}: {
  описание: ReturnType<typeof описаниеПоКоду>
  запись: ЗаписьКарточки
  источник: ИсточникКарточек
  безПравки?: boolean
}) {
  return (
    <Card
      описание={описание}
      запись={запись}
      источник={источник}
      безПравки={безПравки}
      описаниеСущности={(с) => ОПИСАНИЯ[с]}
    />
  )
}
